import { useId } from "react";

import IconButton from "@/components/Button/IconButton";
import Combobox from "@/components/Combobox";
import { CountryFlag, CountryFlagWithLabel } from "@/components/CountryFlag";
import Icon from "@/components/Icon/Icon";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { SupportedCountry } from "@/constants/country";

import type { UseSelectCountryReturn } from "../-hooks/useSelectCountry";
import type { CountryOption } from "../-types";

export function SelectCountryPresenter({
  open,
  setOpen,
  searchText,
  setSearchText,
  countryOptions,
  selectedCountry,
  handleCountryChange,
  onClear,
  t,
}: UseSelectCountryReturn) {
  const popoverContentId = useId();
  const canClear = Boolean(onClear && selectedCountry?.value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative inline-block w-44">
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-controls={popoverContentId}
            className="relative w-full justify-start pr-10"
          >
            <div
              className={
                canClear
                  ? "flex min-w-0 items-center gap-2 pr-8"
                  : "flex min-w-0 items-center gap-2"
              }
            >
              {selectedCountry?.value && (
                <CountryFlag countryCode={selectedCountry.value} />
              )}
              <span className="min-w-0 truncate">
                {selectedCountry?.label ?? t("placeholder")}
              </span>
            </div>
            <div className="absolute inset-y-0 right-3 flex items-center">
              <Icon
                iconName="ChevronDownIcon"
                className="shrink-0 opacity-50"
              />
            </div>
          </Button>
        </PopoverTrigger>
        {canClear && (
          <div className="absolute inset-y-0 right-8 flex items-center">
            <IconButton
              icon="XIcon"
              variant="mutedGhost"
              size="ExtraSmall"
              aria-label="Clear selection"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClear?.();
                setOpen(false);
                setSearchText("");
              }}
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            />
          </div>
        )}
      </div>
      <PopoverContent id={popoverContentId} className="w-60 p-0">
        <Combobox<CountryOption>
          type="single"
          options={countryOptions}
          value={selectedCountry?.value || ""}
          onValueChange={(value: string | null) =>
            handleCountryChange(
              value ? (value as keyof typeof SupportedCountry) : undefined,
            )
          }
          search={searchText}
          onSearchChange={setSearchText}
          searchPlaceholder={t("searchPlaceholder")}
          noResultsMessage={t("noResultsMessage")}
          showCheckbox={false}
          renderOption={({ option }: { option: CountryOption }) => (
            <CountryFlagWithLabel
              countryCode={option.value}
              label={option.label}
            />
          )}
        />
      </PopoverContent>
    </Popover>
  );
}
